import { useState, type FormEvent, type ReactNode } from 'react';
import { ListFilter, Trash2, Users } from 'lucide-react';
import type { Filter } from '@outreach/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { api, ApiRequestError } from '@/lib/api';
import { buildSegmentFilter } from '@/features/organization/buildFilter';
import {
  useAudiencePreview,
  useCreateList,
  useCreateSegment,
  useLists,
  useSegments,
  useTags,
} from '@/features/organization/useOrganization';
import { useQueryClient } from '@tanstack/react-query';

export function SegmentsPage() {
  const lists = useLists();
  const segments = useSegments();
  const qc = useQueryClient();
  const preview = useAudiencePreview();
  const [previews, setPreviews] = useState<
    Record<string, { people: number; messageCount: number }>
  >({});

  async function runPreview(id: string, filter: Filter) {
    try {
      const r = await preview.mutateAsync(filter);
      setPreviews((p) => ({ ...p, [id]: { people: r.people, messageCount: r.messageCount } }));
    } catch {
      toast.error('Could not evaluate segment');
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Fixed lists and saved dynamic segments for targeting.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="size-4 text-muted-foreground" />
              <CardTitle className="text-base">Lists</CardTitle>
            </div>
            <NewListDialog />
          </CardHeader>
          <CardContent className="space-y-2">
            {lists.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {lists.data?.items.length === 0 && (
              <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No lists yet.
              </p>
            )}
            {lists.data?.items.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="text-sm font-medium">{l.name}</p>
                  {l.description && (
                    <p className="text-xs text-muted-foreground">{l.description}</p>
                  )}
                </div>
                <Badge variant="muted">{l.memberCount} members</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <ListFilter className="size-4 text-muted-foreground" />
              <CardTitle className="text-base">Saved segments</CardTitle>
            </div>
            <NewSegmentDialog />
          </CardHeader>
          <CardContent className="space-y-2">
            {segments.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {segments.data?.items.length === 0 && (
              <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No saved segments yet.
              </p>
            )}
            {segments.data?.items.map((s) => {
              const pv = previews[s.id];
              return (
                <div key={s.id} className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    {pv && (
                      <p className="text-xs text-muted-foreground">
                        {pv.people} people · {pv.messageCount} messages
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => runPreview(s.id, s.filter as Filter)}
                    >
                      Preview
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Delete segment ${s.name}`}
                      onClick={async () => {
                        await api.segments.remove(s.id);
                        void qc.invalidateQueries({ queryKey: ['segments'] });
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function NewListDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateList();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ name, description: description || undefined });
      toast.success('List created');
      setName('');
      setDescription('');
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not create list');
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          New list
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New list</DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          <Labeled label="Name" value={name} onChange={setName} required />
          <Labeled label="Description" value={description} onChange={setDescription} />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewSegmentDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [hasEmail, setHasEmail] = useState(true);
  const [emailConsent, setEmailConsent] = useState(true);
  const [tagId, setTagId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const tags = useTags();
  const create = useCreateSegment();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const filter = buildSegmentFilter({ hasEmail, emailConsent, tagId: tagId || undefined });
    if (!filter) {
      setError('Choose at least one condition.');
      return;
    }
    try {
      await create.mutateAsync({ name, filter });
      toast.success('Segment saved');
      setName('');
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not save segment');
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          New segment
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New saved segment</DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          <Labeled label="Name" value={name} onChange={setName} required />
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Match all of</legend>
            <Check label="Has an email address" checked={hasEmail} onChange={setHasEmail} />
            <Check
              label="Email consent recorded"
              checked={emailConsent}
              onChange={setEmailConsent}
            />
            <div className="space-y-1.5">
              <Label htmlFor="tag">In tag (optional)</Label>
              <select
                id="tag"
                value={tagId}
                onChange={(e) => setTagId(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— any —</option>
                {tags.data?.items.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </fieldset>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Save segment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Labeled({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  const id = label.toLowerCase();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} required={required} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): ReactNode {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4"
      />
      {label}
    </label>
  );
}
